import axios from 'axios'
import { useLoadingStore } from '@/stores/loadingStore'
import { useLanguage } from '@/composables/useLanguage'

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: parseInt(import.meta.env.VITE_API_TIMEOUT) || 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  },
  responseType: 'json'
})

let isRefreshing = false
let failedQueue = []
let cachedIp = null

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  isRefreshing = false
  failedQueue = []
}

const getClientIp = async () => {
  if (cachedIp) return cachedIp
  try {
    const response = await fetch('https://api.ipify.org?format=json')
    const data = await response.json()
    cachedIp = data.ip
    return cachedIp
  } catch (error) {
    console.error('[API] Errore nel recupero IP:', error)
    return null
  }
}

apiClient.interceptors.request.use(
  async config => {
    const { currentLanguage } = useLanguage()
    config.headers['Accept-Language'] = currentLanguage.value

    // Aggiungi Bearer Token se presente
    const token = sessionStorage.getItem('token')
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`
    }

    // Recupera l'IP solo se non lo abbiamo già, per non rallentare ogni richiesta
    const clientIp = await getClientIp()
    if (clientIp) {
      config.headers['X-Forwarded-For'] = clientIp
    }
    
    const loadingStore = useLoadingStore()
    loadingStore.show()
    return config
  },
  error => Promise.reject(error)
)

apiClient.interceptors.response.use(
  response => {
    const loadingStore = useLoadingStore()
    loadingStore.hide()
    return response
  },
  error => {
    const { config } = error

    if (!error.response) {
      const loadingStore = useLoadingStore()
      loadingStore.hide()
      return Promise.reject({
        message: 'Impossibile contattare il server. Verifica la connessione.',
        originalError: error,
        isNetworkError: true
      })
    }

    // Gestione errore 401 (Unauthorized) con tentativo di refresh token
    // Con i cookie HttpOnly, non dobbiamo più gestire il token manualmente nel JSON
    const isAuthEndpoint = config.url.includes('/auth/')
    const isPublicEndpoint = [
      '/checkout/summary',
      '/salesorders/draft',
      '/salesorders/guest/',
      '/payments/initialize',
      '/payments/webhook',
      '/payments/session/',
      '/newsletter/subscribe',
      '/newsletter/verify',
      '/newsletter/unsubscribe',
      '/contact'
    ].some(prefix => config.url.startsWith(prefix)) || (config.url.startsWith('/salesorders/') && config.url.endsWith('/status'))
    const hasToken = !!sessionStorage.getItem('token')
    const isCountdownMode = window.__IS_COUNTDOWN_MODE__ === true || 
                          window.location.pathname.includes('/countdown') || 
                          window.location.hash.includes('/countdown')
    
    if (error.response?.status === 401 && !isAuthEndpoint && !isPublicEndpoint && hasToken && !isRefreshing && !config._retry && !isCountdownMode) {
      config._retry = true
      isRefreshing = true

      return apiClient
        .post('/auth/refresh', {}, { withCredentials: true })
        .then(() => {
          processQueue(null)
          return apiClient(config)
        })
        .catch(err => {
          const loadingStore = useLoadingStore()
          loadingStore.hide()
          processQueue(err, null)
          
          // Se anche il refresh fallisce, puliamo la sessione UI
          sessionStorage.removeItem('user')
          
          // In modalità countdown, non forziamo mai il login al refresh fallito
          // Il router si occuperà di proteggere le pagine sensibili
          const isCountdown = window.__IS_COUNTDOWN_MODE__ === true || 
                            window.location.pathname.includes('/countdown') || 
                            window.location.hash.includes('/countdown')
          
          if (!isCountdown && !window.location.pathname.includes('/login') && !window.location.hash.includes('/login')) {
            window.location.href = '/login'
          }
          return Promise.reject(err)
        })
    }

    if (error.response?.status === 401 && isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject })
      })
        .then(() => {
          return apiClient(config)
        })
        .catch(err => {
          const loadingStore = useLoadingStore()
          loadingStore.hide()
          return Promise.reject(err)
        })
    }

    if (error.response?.data && !error.response.data.code && !error.response.data.success) {
      const status = error.response.status
      const data = error.response.data

      if (status === 429) {
        error.response.data = {
          ...data,
          success: false,
          code: data.code || 'AUTH_RATE_LIMITED'
        }
      } else if (status === 401) {
        error.response.data = {
          ...data,
          success: false,
          code: data.code || 'AUTH_INVALID_CREDENTIALS'
        }
      } else if (status === 409) {
        error.response.data = {
          ...data,
          success: false,
          code: data.code || (data.error?.toLowerCase().includes('newsletter') || data.message?.toLowerCase().includes('newsletter') ? 'NEWSLETTER_ALREADY_SUBSCRIBED' : 'AUTH_EMAIL_ALREADY_VERIFIED')
        }
      } else if (status === 400) {
        error.response.data = {
          ...data,
          success: false,
          code: data.code || (data.error?.toLowerCase().includes('privacy') || data.message?.toLowerCase().includes('privacy') ? 'AUTH_PRIVACY_REQUIRED' : 'AUTH_INVALID_REQUEST')
        }
      } else if (status >= 500) {
        error.response.data = {
          ...data,
          success: false,
          code: data.code || 'SYSTEM_ERROR'
        }
      }
    }

    const loadingStore = useLoadingStore()
    loadingStore.hide()
    return Promise.reject(error)
  }
)

export default apiClient
export { getClientIp }
